/*
 * Admin Security Control Center — unified 4-pillar surface (Feb 2026).
 *
 *   • Overview KPI strip
 *   • Tab 1 · Admins & Roles — list every admin, change scope_role
 *   • Tab 2 · Elevations    — temporary 4h-24h elevations + approvals
 *   • Tab 3 · Break-Glass   — peer-approved emergency access
 *   • Tab 4 · Read Audit    — every PII view / sensitive read
 *   • Tab 5 · Exports       — CSV/PDF downloads + per-admin cap
 *   • Tab 6 · Alerts        — fires from the anomaly engine
 *   • Tab 7 · SIEM Export   — NDJSON feed link for Splunk/Datadog
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Shield, ShieldAlert, Eye, Download, AlertTriangle, Users, Clock,
  KeyRound, RefreshCcw, FileJson, BellRing, ArrowUpRight,
} from "lucide-react";

const ROLES = ["support_l1", "support_l2", "finance", "engineering"];
const ROLE_COLOR = {
  support_l1: "bg-sky-50 border-sky-200 text-sky-900",
  support_l2: "bg-violet-50 border-violet-200 text-violet-900",
  finance:    "bg-emerald-50 border-emerald-200 text-emerald-900",
  engineering:"bg-rose-50 border-rose-200 text-rose-900",
};

const TABS = [
  { id: "admins",      label: "Admins & Roles", icon: Users },
  { id: "elevations",  label: "Elevations",     icon: ArrowUpRight },
  { id: "break_glass", label: "Break-Glass",    icon: ShieldAlert },
  { id: "reads",       label: "Read Audit",     icon: Eye },
  { id: "exports",     label: "Exports",        icon: Download },
  { id: "alerts",      label: "Alerts",         icon: BellRing },
  { id: "siem",        label: "SIEM Feed",      icon: FileJson },
];

export default function AdminSecurity() {
  const [tab, setTab] = useState("admins");
  const [overview, setOverview] = useState(null);
  const refresh = async () => {
    try {
      const r = await api.get("/admin/security/overview");
      setOverview(r.data);
    } catch (e) {
      toast.error("Failed to load security overview");
    }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="admin-security-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 chip bg-foreground/5 border-border text-[10.5px] mono mb-1">
            <Shield className="w-3 h-3" /> SECURITY CONTROL CENTER
          </div>
          <h1 className="display text-3xl tracking-tight">Admin Security</h1>
          <p className="text-[12.5px] text-foreground/55 max-w-xl">
            SSO · RBAC · Data access · Audit. All four pillars in one console.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="security-refresh-btn">
          <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </header>

      <OverviewStrip overview={overview} />

      {/* Tabs */}
      <div className="border-b flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`security-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id ? "border-foreground text-foreground" : "border-transparent text-foreground/55 hover:text-foreground/80"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "admins"      && <AdminsTab onChange={refresh} />}
      {tab === "elevations"  && <ElevationsTab onChange={refresh} />}
      {tab === "break_glass" && <BreakGlassTab onChange={refresh} />}
      {tab === "reads"       && <ReadsTab />}
      {tab === "exports"     && <ExportsTab />}
      {tab === "alerts"      && <AlertsTab onChange={refresh} />}
      {tab === "siem"        && <SiemTab />}
    </div>
  );
}

function OverviewStrip({ overview }) {
  const cells = [
    { k: "admins_total",       label: "Admin users",       v: overview?.admins_total       ?? "—", icon: Users },
    { k: "elevations_active",  label: "Active elevations", v: overview?.elevations_active  ?? "—", icon: ArrowUpRight },
    { k: "break_glass_open",   label: "Break-glass open",  v: overview?.break_glass_open   ?? "—", icon: ShieldAlert, accent: (overview?.break_glass_open || 0) > 0 },
    { k: "alerts_open",        label: "Alerts open",       v: overview?.alerts_open        ?? "—", icon: BellRing,    accent: (overview?.alerts_open || 0) > 0 },
    { k: "alerts_high_24h",    label: "High-severity 24h", v: overview?.alerts_high_24h    ?? "—", icon: AlertTriangle, accent: (overview?.alerts_high_24h || 0) > 0 },
    { k: "overrides_24h",      label: "Overrides 24h",     v: overview?.overrides_24h      ?? "—", icon: Shield },
    { k: "reads_24h",          label: "PII reads 24h",     v: overview?.reads_24h          ?? "—", icon: Eye },
    { k: "exports_7d",         label: "Exports 7d",        v: overview?.exports_7d         ?? "—", icon: Download },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="security-overview-strip">
      {cells.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.k} data-testid={`security-tile-${c.k}`}
            className={`rounded-2xl border px-4 py-3 ${c.accent ? "border-amber-300 bg-amber-50" : "border-border bg-card"}`}>
            <div className="flex items-center gap-2 text-[11px] text-foreground/55 uppercase tracking-wider">
              <Icon className="w-3.5 h-3.5" /> {c.label}
            </div>
            <div className="mt-1 display text-2xl">{c.v}</div>
          </div>
        );
      })}
    </div>
  );
}

function AdminsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // admin row
  const [reason, setReason] = useState("");
  const [toRole, setToRole] = useState("support_l1");
  const load = async () => {
    try {
      const r = await api.get("/admin/security/admins");
      setRows(r.data?.admins || []);
    } catch (e) { toast.error("Failed to load admins"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!reason || reason.length < 3) { toast.error("Reason ≥3 chars required"); return; }
    try {
      await api.post(`/admin/security/admins/${editing.id}/scope`, { role: toRole, reason });
      toast.success(`Scope role updated → ${toRole}`);
      setEditing(null); setReason(""); load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <Card data-testid="security-admins-card">
      <CardHeader><CardTitle className="text-base">Admin users</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-foreground/60 text-[11px] uppercase tracking-wider">
              <tr className="text-left">
                <th className="py-2">Email</th><th>Scope role</th><th>Active elevation</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`security-admin-row-${r.id}`}>
                  <td className="py-2.5 pr-3">
                    <div className="font-medium">{r.email}</div>
                    <div className="text-[11px] text-foreground/50">{r.name || r.company}</div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={`px-2 py-0.5 rounded border text-[11px] ${ROLE_COLOR[r.scope_role] || ""}`}>{r.scope_role}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-[12px]">
                    {r.active_elevation ? (
                      <div className="flex items-center gap-1 text-amber-700">
                        <Clock className="w-3 h-3" />
                        → {r.active_elevation.to_role} · exp {String(r.active_elevation.expires_at || "").slice(0, 16)}
                      </div>
                    ) : <span className="text-foreground/40">—</span>}
                  </td>
                  <td className="py-2.5 text-right">
                    <Button size="sm" variant="outline"
                      data-testid={`security-edit-role-${r.id}`}
                      onClick={() => { setEditing(r); setToRole(r.scope_role); setReason(""); }}>
                      Change role
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-foreground/50">No admins</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="security-role-dialog">
          <DialogHeader><DialogTitle>Change scope role · {editing?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New role</Label>
              <Select value={toRole} onValueChange={setToRole}>
                <SelectTrigger data-testid="security-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason (audited)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="security-role-reason" placeholder="why this change?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={submit} data-testid="security-role-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ElevationsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ target_admin_id: "", to_role: "support_l2", hours: 4, reason: "", auto_approve_self: true });
  const [admins, setAdmins] = useState([]);
  const load = async () => {
    try {
      const r = await api.get("/admin/security/elevations");
      setRows(r.data?.elevations || []);
      const a = await api.get("/admin/security/admins");
      setAdmins(a.data?.admins || []);
    } catch (e) { toast.error("Failed to load elevations"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.target_admin_id) { toast.error("Select target admin"); return; }
    if (!form.reason || form.reason.length < 5) { toast.error("Reason ≥5 chars"); return; }
    try {
      await api.post("/admin/security/elevations", form);
      toast.success("Elevation requested");
      setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed"); }
  };

  const approve = async (id) => {
    try { await api.post(`/admin/security/elevations/${id}/approve`); toast.success("Approved"); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const revoke = async (id) => {
    const reason = prompt("Revoke reason");
    if (!reason || reason.length < 3) return;
    try { await api.post(`/admin/security/elevations/${id}/revoke`, { reason }); toast.success("Revoked"); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  return (
    <Card data-testid="security-elevations-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Temporary elevations</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="security-new-elevation-btn">+ New elevation</Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-foreground/60 text-[11px] uppercase tracking-wider">
              <tr className="text-left"><th>Target</th><th>From → To</th><th>Hours</th><th>Status</th><th>Expires</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`security-elev-row-${r.id}`}>
                  <td className="py-2.5 pr-3">{r.target_admin_email}</td>
                  <td className="py-2.5 pr-3 text-[12px]">{r.base_role} → <strong>{r.to_role}</strong></td>
                  <td className="py-2.5 pr-3">{r.hours}h</td>
                  <td className="py-2.5 pr-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] ${
                      r.status === "active" ? "bg-emerald-100 text-emerald-900" :
                      r.status === "pending" ? "bg-amber-100 text-amber-900" :
                      r.status === "revoked" ? "bg-slate-100 text-slate-700" : "bg-rose-100 text-rose-900"
                    }`}>{r.status}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-[12px] text-foreground/60">{String(r.expires_at || "").slice(0, 16)}</td>
                  <td className="py-2.5 text-right space-x-1">
                    {r.status === "pending" && (
                      <Button size="sm" variant="outline" onClick={() => approve(r.id)} data-testid={`security-approve-${r.id}`}>Approve</Button>
                    )}
                    {r.status === "active" && (
                      <Button size="sm" variant="outline" onClick={() => revoke(r.id)} data-testid={`security-revoke-${r.id}`}>Revoke</Button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-foreground/50">No elevations yet</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="security-new-elev-dialog">
          <DialogHeader><DialogTitle>Request temporary elevation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Target admin</Label>
              <Select value={form.target_admin_id} onValueChange={(v) => setForm({ ...form, target_admin_id: v })}>
                <SelectTrigger data-testid="security-elev-target"><SelectValue placeholder="Pick admin" /></SelectTrigger>
                <SelectContent>
                  {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Elevate to role</Label>
              <Select value={form.to_role} onValueChange={(v) => setForm({ ...form, to_role: v })}>
                <SelectTrigger data-testid="security-elev-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Duration (hours, max 24)</Label>
              <Input type="number" min={1} max={24} value={form.hours}
                onChange={(e) => setForm({ ...form, hours: parseInt(e.target.value) || 4 })}
                data-testid="security-elev-hours" />
            </div>
            <div>
              <Label>Reason (≥5 chars)</Label>
              <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="security-elev-reason" />
            </div>
            <label className="flex items-center gap-2 text-[12.5px]">
              <input type="checkbox" checked={form.auto_approve_self}
                onChange={(e) => setForm({ ...form, auto_approve_self: e.target.checked })}
                data-testid="security-elev-auto" />
              Auto-approve (engineering admins only)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="security-elev-submit">Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function BreakGlassTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("");
  const [reason, setReason] = useState("");
  const load = async () => {
    try { const r = await api.get("/admin/security/break-glass"); setRows(r.data?.requests || []); }
    catch (e) { toast.error("Failed to load break-glass requests"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!scope || scope.length < 3) { toast.error("Scope required"); return; }
    if (!reason || reason.length < 10) { toast.error("Reason ≥10 chars"); return; }
    try {
      await api.post("/admin/security/break-glass", { scope_required: scope, reason });
      toast.success("Break-glass request opened");
      setOpen(false); setScope(""); setReason(""); load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed"); }
  };
  const approve = async (id) => {
    try { await api.post(`/admin/security/break-glass/${id}/approve`); toast.success("Approved"); load(); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };
  const close = async (id) => {
    const notes = prompt("Resolution notes (optional)") || "";
    try { await api.post(`/admin/security/break-glass/${id}/close`, { notes }); toast.success("Closed"); load(); onChange?.(); }
    catch (e) { toast.error("Close failed"); }
  };

  return (
    <Card data-testid="security-bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Break-glass requests</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="security-new-bg-btn">
          <ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Open break-glass
        </Button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-foreground/60 text-[11px] uppercase tracking-wider">
              <tr className="text-left"><th>Requester</th><th>Scope</th><th>Reason</th><th>Status</th><th>Opened</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" data-testid={`security-bg-row-${r.id}`}>
                  <td className="py-2.5 pr-3">{r.requester_email}</td>
                  <td className="py-2.5 pr-3 text-[12px]"><code>{r.scope_required}</code></td>
                  <td className="py-2.5 pr-3 max-w-[280px] truncate" title={r.reason}>{r.reason}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] ${
                      r.status === "pending" ? "bg-amber-100 text-amber-900" :
                      r.status === "active"  ? "bg-emerald-100 text-emerald-900" :
                      "bg-slate-100 text-slate-700"
                    }`}>{r.status}</span>
                  </td>
                  <td className="py-2.5 pr-3 text-[12px] text-foreground/60">{String(r.opened_at || "").slice(0, 16)}</td>
                  <td className="py-2.5 text-right space-x-1">
                    {r.status === "pending" && (
                      <Button size="sm" variant="outline" onClick={() => approve(r.id)} data-testid={`security-bg-approve-${r.id}`}>Approve</Button>
                    )}
                    {(r.status === "pending" || r.status === "active") && (
                      <Button size="sm" variant="outline" onClick={() => close(r.id)} data-testid={`security-bg-close-${r.id}`}>Close</Button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-foreground/50">No break-glass requests</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="security-new-bg-dialog">
          <DialogHeader><DialogTitle>Open break-glass request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Scope required</Label>
              <Input value={scope} onChange={(e) => setScope(e.target.value)} placeholder="e.g. admin.wallet.credit" data-testid="security-bg-scope" />
            </div>
            <div>
              <Label>Reason (≥10 chars)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="describe the incident" data-testid="security-bg-reason" />
            </div>
            <div className="text-[11.5px] text-foreground/55 bg-amber-50 border border-amber-200 rounded p-2">
              Peer approval required. Another engineering-tier admin must approve. Max 4h.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="security-bg-submit">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function ReadsTab() {
  const [rows, setRows] = useState([]);
  const [hours, setHours] = useState(24);
  const load = async () => {
    try { const r = await api.get(`/admin/security/read-audit?since_hours=${hours}`); setRows(r.data?.reads || []); }
    catch (e) { toast.error("Failed to load reads"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours]);
  return (
    <Card data-testid="security-reads-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">PII / sensitive reads</CardTitle>
        <Select value={String(hours)} onValueChange={(v) => setHours(parseInt(v))}>
          <SelectTrigger className="w-[160px]" data-testid="security-reads-window"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="24">Last 24h</SelectItem>
            <SelectItem value="168">Last 7d</SelectItem>
            <SelectItem value="720">Last 30d</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-foreground/60 text-[11px] uppercase tracking-wider">
              <tr className="text-left"><th>When</th><th>Admin</th><th>Resource</th><th>Fields</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-2 pr-3 text-foreground/60">{String(r.ts || "").slice(0, 19)}</td>
                  <td className="py-2 pr-3">{r.admin_email}</td>
                  <td className="py-2 pr-3"><code>{r.resource_kind}{r.resource_id ? `:${String(r.resource_id).slice(-6)}` : ""}</code></td>
                  <td className="py-2 pr-3 text-[11px]">{(r.fields || []).join(", ")}</td>
                  <td className="py-2 pr-3 max-w-[280px] truncate" title={r.reason || ""}>{r.reason || "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-foreground/50">No reads in this window</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ExportsTab() {
  const [rows, setRows] = useState([]);
  const [cap, setCap] = useState(null);
  const load = async () => {
    try {
      const r = await api.get("/admin/security/export-audit");
      setRows(r.data?.exports || []);
      const c = await api.get("/admin/security/export-cap");
      setCap(c.data);
    } catch (e) { toast.error("Failed to load exports"); }
  };
  useEffect(() => { load(); }, []);
  return (
    <Card data-testid="security-exports-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Data export audit</CardTitle>
        {cap && (
          <div className="text-[11.5px] text-foreground/60" data-testid="security-export-cap">
            Your cap: <strong>{cap.used_last_hour}</strong> / {cap.cap_per_hour} rows last hour · role <code>{cap.scope_role}</code>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-foreground/60 text-[11px] uppercase tracking-wider">
              <tr className="text-left"><th>When</th><th>Admin</th><th>Dataset</th><th>Rows</th><th>Fmt</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="py-2 pr-3 text-foreground/60">{String(r.ts || "").slice(0, 19)}</td>
                  <td className="py-2 pr-3">{r.admin_email}</td>
                  <td className="py-2 pr-3"><code>{r.dataset}</code></td>
                  <td className="py-2 pr-3 tabular-nums">{r.rows?.toLocaleString?.() || r.rows}</td>
                  <td className="py-2 pr-3">{r.fmt}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-foreground/50">No exports yet</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function AlertsTab({ onChange }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/admin/security/alerts?since_hours=168"); setRows(r.data?.alerts || []); }
    catch (e) { toast.error("Failed to load alerts"); }
  };
  useEffect(() => { load(); }, []);
  const scan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/security/alerts/scan", { window_min: 60 });
      toast.success(`Scan complete · ${r.data?.new_alerts ?? 0} new`);
      load(); onChange?.();
    } catch (e) { toast.error("Scan failed"); }
    finally { setBusy(false); }
  };
  const resolve = async (id) => {
    const note = prompt("Resolution note");
    if (!note || note.length < 3) return;
    try { await api.post(`/admin/security/alerts/${id}/resolve`, { note }); toast.success("Resolved"); load(); onChange?.(); }
    catch (e) { toast.error("Resolve failed"); }
  };
  return (
    <Card data-testid="security-alerts-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Recent alerts</CardTitle>
        <Button size="sm" onClick={scan} disabled={busy} data-testid="security-alerts-scan-btn">
          {busy ? <RefreshCcw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5 mr-1.5" />}
          Run scan now
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.id} className="border rounded-lg p-3 flex items-start gap-3" data-testid={`security-alert-${a.id}`}>
              <div className={`mt-0.5 ${
                a.severity === "high" ? "text-rose-700" :
                a.severity === "medium" ? "text-amber-700" : "text-slate-600"
              }`}>
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <strong className="text-[13px]">{a.title}</strong>
                  <span className={`px-1.5 py-0.5 rounded text-[10.5px] ${
                    a.severity === "high" ? "bg-rose-100 text-rose-900" :
                    a.severity === "medium" ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"
                  }`}>{a.severity}</span>
                  <span className="text-[10.5px] text-foreground/40 mono">{a.rule}</span>
                  <span className={`ml-auto px-1.5 py-0.5 rounded text-[10.5px] ${
                    a.status === "open" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"
                  }`}>{a.status}</span>
                </div>
                <div className="text-[12px] text-foreground/65 mt-0.5">{a.body}</div>
                <div className="text-[10.5px] text-foreground/40 mt-1">{String(a.ts || a.created_at || "").slice(0, 19)}</div>
              </div>
              {a.status === "open" && (
                <Button size="sm" variant="outline" onClick={() => resolve(a.id)} data-testid={`security-alert-resolve-${a.id}`}>
                  Resolve
                </Button>
              )}
            </div>
          ))}
          {rows.length === 0 && <div className="py-8 text-center text-foreground/50">No alerts in the last 7 days</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function SiemTab() {
  const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api/admin/security/siem/export.ndjson";
  return (
    <Card data-testid="security-siem-card">
      <CardHeader><CardTitle className="text-base">SIEM event stream</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-[13px]">
        <p className="text-foreground/65">
          NDJSON feed of every admin override, read, export, and alert. One JSON object per line —
          stream into Splunk / Datadog / Sumo Logic with the configured high-water mark.
        </p>
        <div className="bg-slate-50 border rounded p-3 mono text-[11.5px] break-all">{base}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" asChild data-testid="security-siem-download">
            <a href={base} target="_blank" rel="noreferrer">Download last 24h</a>
          </Button>
          <Button size="sm" variant="outline" asChild data-testid="security-siem-week">
            <a href={`${base}?since=${encodeURIComponent(new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString())}`} target="_blank" rel="noreferrer">
              Download last 7d
            </a>
          </Button>
        </div>
        <p className="text-[11.5px] text-foreground/55">
          Auth: requires the same admin JWT cookie or <code>Authorization: Bearer ...</code>.
          Filter via <code>?kinds=admin_override,admin_alert</code>.
        </p>
      </CardContent>
    </Card>
  );
}
